import type { IStorageService } from "@/application/ports/services";
import { logger } from "@/infrastructure/logger";

export class LocalStorageService implements IStorageService {
	get<T>(key: string): T | null {
		try {
			const item = localStorage.getItem(key);
			if (item === null) {
				return null;
			}

			// Try to parse as JSON first
			try {
				return JSON.parse(item) as T;
			} catch {
				// If JSON parsing fails, return the raw string (for tokens and simple strings)
				// This handles the case where tokens are stored as plain strings
				logger.debug(
					`Item in localStorage (key: ${key}) is not JSON, returning as string`,
				);
				return item as T;
			}
		} catch (error) {
			logger.error(`Error reading from localStorage (key: ${key}):`, error);
			return null;
		}
	}

	set<T>(key: string, value: T): void {
		try {
			// If the value is a string, store it directly (for tokens)
			// If it's an object, stringify it
			if (typeof value === "string") {
				localStorage.setItem(key, value);
			} else {
				localStorage.setItem(key, JSON.stringify(value));
			}
		} catch (error) {
			logger.error(`Error writing to localStorage (key: ${key}):`, error);
			throw error;
		}
	}

	remove(key: string): void {
		try {
			localStorage.removeItem(key);
		} catch (error) {
			logger.error(`Error removing from localStorage (key: ${key}):`, error);
		}
	}

	clear(): void {
		try {
			localStorage.clear();
		} catch (error) {
			logger.error("Error clearing localStorage:", error);
		}
	}

	exists(key: string): boolean {
		try {
			return localStorage.getItem(key) !== null;
		} catch (error) {
			logger.error(
				`Error checking localStorage existence (key: ${key}):`,
				error,
			);
			return false;
		}
	}
}
